import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Box, Button, HStack, Heading, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Text, VStack, useDisclosure, useToast, Alert, AlertIcon,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  ModalFooter, Tabs, TabList, TabPanels, Tab, TabPanel, Code, Stat, StatLabel,
  StatNumber, SimpleGrid, Divider, Tooltip, Select, Switch, Input,
} from '@chakra-ui/react';
import { FiRefreshCw, FiPlus, FiLink } from 'react-icons/fi';
import { Trans, useTranslation } from 'react-i18next';

import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import channexService from '../services/channexService';
import apiClient from '../services/apiClient';
import AddHotelModal from '../components/channex/AddHotelModal';
import ConnectChannelModal from '../components/channex/ConnectChannelModal';
import LoggedBookingsModal from '../components/channex/LoggedBookingsModal';

const QUEUE_COLORS = { pending: 'yellow', done: 'green', dead: 'red', in_flight: 'blue' };

// Sentinel for the hotel filter: this screen is cross-hotel tooling, so
// seeing every config must stay reachable — it is just not the default.

/**
 * Channex channel-manager operations. SUPERUSER ONLY.
 *
 * The route and nav entry are hidden for non-superusers, but the real gate is
 * server-side (IsSuperUser) — these actions provision billable properties,
 * touch credentials, and force syncs that change what every connected OTA
 * sells.
 */
const ChannelManager = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mappings, setMappings] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const [mappingScreenUrl, setMappingScreenUrl] = useState(null);
  // Default to the logged-in hotel; '' until the configs load, so the first
  // render can resolve it against hotels that actually have a Channex config
  // (a superuser whose own hotel has none must not land on an empty screen).

  const mappingModal = useDisclosure();
  const addHotelModal = useDisclosure();
  const connectModal = useDisclosure();
  const confirmModal = useDisclosure();
  const mappingScreenModal = useDisclosure();
  const loggedBookingsModal = useDisclosure();
  const toast = useToast();
  const { t } = useTranslation();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await channexService.list();
      const loaded = data.configs || [];
      setConfigs(loaded);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t('channelManager.errors.loadConfigs'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // The API now returns only the hotel the superadmin is signed into, so
  // the list is shown as-is; no cross-hotel filter in the UI (use Switch
  // property to change hotels). scopedHotelName labels the header.
  const scopedHotelName = configs[0]?.hotel?.name || '';
  const visibleConfigs = configs;

  const run = async (config, label, fn, { reload = true } = {}) => {
    setBusyId(config.id);
    try {
      const result = await fn();
      // connected:false covers the test-connection endpoint, which reports
      // a failed credential check with HTTP 200.
      const failed = result?.success === false || result?.connected === false;
      toast({
        title: failed
          ? t('channelManager.toasts.actionFailed', { action: label })
          : t('channelManager.toasts.actionComplete', { action: label }),
        description: result?.message || result?.summary || describe(result),
        status: failed ? 'warning' : 'success',
        duration: 6000,
        isClosable: true,
      });
      if (reload) await load();
      return result;
    } catch (err) {
      toast({
        title: t('channelManager.toasts.actionFailed', { action: label }),
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setBusyId(null);
    }
  };

  const describe = (result) => {
    if (!result) return '';
    if (result.queued) {
      return t('channelManager.results.queued', {
        availability: result.queued.availability_rows, rates: result.queued.rate_rows,
      });
    }
    if (typeof result.fetched === 'number') {
      return t('channelManager.results.fetched', {
        fetched: result.fetched, imported: result.imported, acked: result.acked,
      });
    }
    if (Array.isArray(result.created) && Array.isArray(result.primary_set)) {
      return t('channelManager.results.mappingsPrepared', {
        created: result.created.length, primary: result.primary_set.length,
      });
    }
    return '';
  };

  // Edit one prepared rate mapping row in place, then refresh the table.
  const editRateMapping = async (row, body) => {
    try {
      await channexService.updateRateMapping(selected.id, { id: row.id, ...body });
      setMappings(await channexService.getMappings(selected.id));
    } catch (err) {
      toast({
        title: t('channelManager.toasts.updateFailed'),
        description: err.response?.data?.detail || err.message,
        status: 'error',
      });
    }
  };

  const openMappings = async (config) => {
    setSelected(config);
    setMappings(null);
    mappingModal.onOpen();
    try {
      setMappings(await channexService.getMappings(config.id));
    } catch (err) {
      toast({ title: t('channelManager.toasts.loadMappingsFailed'), description: err.message, status: 'error' });
      // Without this the modal shows "Loading…" forever after a failure.
      setMappings({ error: err.response?.data?.detail || err.message });
    }
  };

  // Channex's own white-label mapping screen, embedded. The one-time token
  // behind the URL is single use, so every open mints a fresh one.
  const openMappingScreen = async (config) => {
    setBusyId(config.id);
    try {
      const data = await channexService.mappingScreen(config.id);
      setSelected(config);
      setMappingScreenUrl(data.iframe_url);
      mappingScreenModal.onOpen();
    } catch (err) {
      toast({
        title: t('channelManager.toasts.mappingScreenFailed'),
        description: err.response?.data?.detail || err.response?.data?.message || err.message,
        status: 'error', duration: 8000,
      });
    } finally {
      setBusyId(null);
    }
  };

  // Actions that change what OTAs sell get a confirmation step.
  const confirm = (config, label, message, fn) => {
    setConfirmAction({ config, label, message, fn });
    confirmModal.onOpen();
  };

  const runConfirmed = async () => {
    const { config, label, fn } = confirmAction;
    confirmModal.onClose();
    await run(config, label, fn);
    setConfirmAction(null);
  };

  return (
    <PageShell
      title={t('channelManager.title')}
      subtitle={scopedHotelName
        ? t('channelManager.subtitleWithHotel', { hotel: scopedHotelName })
        : t('channelManager.subtitle')}
      breadcrumbs={[{ label: t('channelManager.breadcrumb'), href: '/channel-manager' }]}
      loading={loading}
      error={error}
      actions={
        <HStack>
          <Button leftIcon={<FiRefreshCw />} size="sm" onClick={load} isLoading={loading}>
            {t('channelManager.buttons.refresh')}
          </Button>
          <Button leftIcon={<FiPlus />} size="sm" colorScheme="blue"
                  onClick={addHotelModal.onOpen}>
            {t('channelManager.buttons.addHotel')}
          </Button>
        </HStack>
      }
    >
      {!loading && configs.length === 0 && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {t('channelManager.emptyState.noConfig')}
        </Alert>
      )}

      <VStack align="stretch" spacing={5}>
        {visibleConfigs.map((config) => (
          <Box key={config.id} borderWidth="1px" borderRadius="lg" p={4} bg="white">
            <HStack justify="space-between" align="start" mb={3}>
              <Box>
                <Heading size="sm">{config.hotel.name}</Heading>
                <Text fontSize="xs" color="gray.500">
                  {t('channelManager.config.meta', {
                    name: config.name, configId: config.id, hotelId: config.hotel.id,
                  })}
                </Text>
              </Box>
              <HStack spacing={2}>
                {config.is_quarantined && (
                  <Tooltip label={t('channelManager.tooltips.quarantined')}>
                    <Badge colorScheme="red">{t('channelManager.badges.quarantined')}</Badge>
                  </Tooltip>
                )}
                <Badge colorScheme={config.is_active ? 'green' : 'gray'}>
                  {config.is_active ? t('channelManager.badges.active') : t('channelManager.badges.inactive')}
                </Badge>
                <Badge colorScheme={config.has_api_key ? 'green' : 'red'}>
                  {config.has_api_key ? t('channelManager.badges.apiKeySet') : t('channelManager.badges.noApiKey')}
                </Badge>
                <Badge colorScheme={config.webhook_registered ? 'green' : 'yellow'}>
                  {config.webhook_registered ? t('channelManager.badges.webhook') : t('channelManager.badges.noWebhook')}
                </Badge>
                <Badge colorScheme={config.messaging_enabled ? 'green' : 'gray'}>
                  {config.messaging_enabled ? t('channelManager.badges.guestChat') : t('channelManager.badges.noGuestChat')}
                </Badge>
                <Badge colorScheme={config.card_payments_connected ? 'green' : config.card_payments_enabled ? 'yellow' : 'gray'}>
                  {config.card_payments_connected
                    ? t('channelManager.badges.cardPaymentsConnected')
                    : config.card_payments_enabled
                      ? t('channelManager.badges.cardPaymentsNoStripe')
                      : t('channelManager.badges.noCardPayments')}
                </Badge>
              </HStack>
            </HStack>

            {config.is_quarantined && (
              <Alert status="error" borderRadius="md" mb={3} fontSize="sm">
                <AlertIcon />
                <Box flex={1}>
                  <Trans i18nKey="channelManager.quarantine.stopped" components={{ code: <Code /> }} />
                  {config.last_sync?.error && (
                    <Text mt={1} fontSize="xs" color="red.700">
                      {t('channelManager.quarantine.lastError', { error: config.last_sync.error })}
                    </Text>
                  )}
                </Box>
                <Button
                  size="xs"
                  colorScheme="red"
                  isLoading={busyId === config.id}
                  onClick={() => run(config, t('channelManager.actions.clearQuarantine'),
                    () => channexService.clearQuarantine(config.id))}
                >
                  {t('channelManager.buttons.clear')}
                </Button>
              </Alert>
            )}

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">{t('channelManager.stats.roomTypesMapped')}</StatLabel>
                <StatNumber fontSize="lg">{config.room_type_mappings}</StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">{t('channelManager.stats.rateMappings')}</StatLabel>
                <StatNumber fontSize="lg">{config.rate_mappings}</StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">{t('channelManager.stats.ariQueue')}</StatLabel>
                <StatNumber fontSize="sm">
                  {Object.keys(config.queue || {}).length === 0
                    ? <Text as="span" color="gray.400">{t('channelManager.stats.queueEmpty')}</Text>
                    : Object.entries(config.queue).map(([k, v]) => (
                        <Badge key={k} mr={1} colorScheme={QUEUE_COLORS[k] || 'gray'}>
                          {k} {v}
                        </Badge>
                      ))}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">{t('channelManager.stats.channexProperty')}</StatLabel>
                <StatNumber fontSize="xs">
                  {config.channex_property_id
                    ? <Code fontSize="xs">{config.channex_property_id.slice(0, 8)}…</Code>
                    : <Text as="span" color="red.500">{t('channelManager.stats.notProvisioned')}</Text>}
                </StatNumber>
              </Stat>
            </SimpleGrid>

            {config.last_sync && (
              <Text fontSize="xs" color="gray.500" mb={3}>
                {config.last_sync.started_at
                  ? t('channelManager.config.lastSyncAt', {
                    operation: config.last_sync.operation,
                    status: config.last_sync.status,
                    time: new Date(config.last_sync.started_at).toLocaleString(),
                  })
                  : t('channelManager.config.lastSync', {
                    operation: config.last_sync.operation,
                    status: config.last_sync.status,
                  })}
              </Text>
            )}

            <Divider mb={3} />

            <HStack spacing={2} flexWrap="wrap">
              <Button size="xs" isLoading={busyId === config.id}
                onClick={() => run(config, t('channelManager.actions.testConnection'),
                  () => channexService.testConnection(config.id), { reload: false })}>
                {t('channelManager.actions.testConnection')}
              </Button>
              <Button size="xs" isLoading={busyId === config.id}
                onClick={() => run(config, t('channelManager.actions.provisionDryRun'),
                  () => channexService.provision(config.id, true), { reload: false })}>
                {t('channelManager.actions.provisionDryRun')}
              </Button>
              <Button size="xs" colorScheme="blue" isLoading={busyId === config.id}
                onClick={() => confirm(config, t('channelManager.actions.provision'),
                  config.channex_property_id
                    ? t('channelManager.confirm.provisionResync')
                    : t('channelManager.confirm.provisionCreate'),
                  () => channexService.provision(config.id, false))}>
                {t('channelManager.actions.provision')}
              </Button>
              <Tooltip label={t('channelManager.tooltips.prepareMappings')}>
                <Button size="xs" colorScheme="teal" variant="outline"
                  isLoading={busyId === config.id}
                  onClick={() => confirm(config, t('channelManager.actions.prepareMappings'),
                    t('channelManager.confirm.prepareMappings'),
                    () => channexService.prepareMappings(config.id, { enable: true }))}>
                  {t('channelManager.actions.prepareMappings')}
                </Button>
              </Tooltip>
              <Button size="xs" colorScheme="blue" variant="outline"
                isDisabled={!config.channex_property_id}
                isLoading={busyId === config.id}
                onClick={() => confirm(config, t('channelManager.actions.catalogueSync'),
                  t('channelManager.confirm.catalogueSync'),
                  () => channexService.syncCatalog(config.id, false))}>
                {t('channelManager.buttons.syncCatalogue')}
              </Button>
              <Button size="xs" colorScheme="orange" variant="outline"
                isDisabled={!config.channex_property_id}
                isLoading={busyId === config.id}
                onClick={() => confirm(config, t('channelManager.actions.fullSync'),
                  t('channelManager.confirm.fullSync'),
                  () => channexService.fullSync(config.id))}>
                {t('channelManager.buttons.queueFullSync')}
              </Button>
              <Button size="xs" variant="outline" isLoading={busyId === config.id}
                onClick={() => run(config, t('channelManager.actions.pollBookings'),
                  () => channexService.pollBookings(config.id))}>
                {t('channelManager.actions.pollBookings')}
              </Button>
              <Button size="xs" variant="outline"
                isDisabled={!config.channex_property_id}
                onClick={() => { setSelected(config); loggedBookingsModal.onOpen(); }}>
                {t('channelManager.buttons.loggedBookings')}
              </Button>
              {config.messaging_enabled ? (
                <Button size="xs" variant="outline" colorScheme="gray"
                  isLoading={busyId === config.id}
                  onClick={() => confirm(config, t('channelManager.actions.disableChat'),
                    t('channelManager.confirm.disableChat'),
                    () => channexService.disableMessaging(config.id))}>
                  {t('channelManager.actions.disableChat')}
                </Button>
              ) : (
                <Button size="xs" variant="outline" colorScheme="teal"
                  isDisabled={!config.channex_property_id}
                  isLoading={busyId === config.id}
                  onClick={() => confirm(config, t('channelManager.actions.enableChat'),
                    t('channelManager.confirm.enableChat'),
                    () => channexService.enableMessaging(config.id))}>
                  {t('channelManager.actions.enableChat')}
                </Button>
              )}
              {config.card_payments_enabled ? (
                <Button size="xs" variant="outline" colorScheme="gray"
                  isLoading={busyId === config.id}
                  onClick={() => confirm(config, t('channelManager.actions.disableCardPayments'),
                    t('channelManager.confirm.disableCardPayments'),
                    () => channexService.disableCardPayments(config.id))}>
                  {t('channelManager.actions.disableCardPayments')}
                </Button>
              ) : (
                <Button size="xs" variant="outline" colorScheme="teal"
                  isDisabled={!config.channex_property_id}
                  isLoading={busyId === config.id}
                  onClick={() => confirm(config, t('channelManager.actions.enableCardPayments'),
                    t('channelManager.confirm.enableCardPayments'),
                    () => channexService.enableCardPayments(config.id))}>
                  {t('channelManager.actions.enableCardPayments')}
                </Button>
              )}
              <Button size="xs" colorScheme="purple" variant="outline"
                leftIcon={<FiLink />}
                isDisabled={!config.channex_property_id}
                onClick={() => { setSelected(config); connectModal.onOpen(); }}>
                {t('channelManager.buttons.connectChannel')}
              </Button>
              <Button size="xs" colorScheme="purple"
                isDisabled={!config.channex_property_id}
                isLoading={busyId === config.id}
                onClick={() => openMappingScreen(config)}>
                {t('channelManager.buttons.mappingScreen')}
              </Button>
              <Button size="xs" variant="ghost" onClick={() => openMappings(config)}>
                {t('channelManager.buttons.viewMappings')}
              </Button>
            </HStack>
          </Box>
        ))}
      </VStack>

      {/* Channex's own channel/mapping UI, embedded. Covers all 57 OTAs
          including the non-standard pipelines (Airbnb OAuth etc.) without
          us maintaining a form per channel. Scoped to one property. */}
      <Modal
        isOpen={mappingScreenModal.isOpen}
        onClose={() => { setMappingScreenUrl(null); mappingScreenModal.onClose(); load(); }}
        size="6xl">
        <ModalOverlay />
        <ModalContent h="85vh">
          <ModalHeader>
            {t('channelManager.mappingScreen.title', { hotel: selected?.hotel?.name })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} display="flex">
            {mappingScreenUrl ? (
              <Box as="iframe" src={mappingScreenUrl}
                   title={t('channelManager.buttons.mappingScreen')}
                   flex="1" w="100%" border="0" />
            ) : (
              <Text fontSize="sm" color="gray.500" p={4}>{t('channelManager.loading')}</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Mappings */}
      <Modal isOpen={mappingModal.isOpen} onClose={mappingModal.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('channelManager.mappings.title', { hotel: selected?.hotel?.name })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!mappings ? <Text fontSize="sm" color="gray.500">{t('channelManager.loading')}</Text>
            : mappings.error ? (
              <Text fontSize="sm" color="red.500">
                {t('channelManager.mappings.loadFailed', { error: mappings.error })}
              </Text>
            ) : (
              <Tabs size="sm" variant="enclosed">
                <TabList>
                  <Tab>{t('channelManager.mappings.roomTypesTab', { n: mappings.room_types.length })}</Tab>
                  <Tab>{t('channelManager.mappings.ratePlansTab', { n: mappings.rate_plans.length })}</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <TableContainer>
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>{t('channelManager.mappings.roomType')}</Th>
                            <Th>{t('channelManager.mappings.channexUuid')}</Th>
                            <Th>{t('channelManager.mappings.active')}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {mappings.room_types.map((m) => (
                            <Tr key={m.id}>
                              <Td>{m.room_type_name}</Td>
                              <Td><Code fontSize="xs">{m.channel_code}</Code></Td>
                              <Td>
                                <Badge colorScheme={m.is_active ? 'green' : 'gray'}>
                                  {m.is_active ? t('channelManager.mappings.yes') : t('channelManager.mappings.no')}
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </TabPanel>
                  <TabPanel px={0}>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      {t('channelManager.mappings.ratePlansHelp')}
                    </Text>
                    <HStack mb={2} spacing={3}>
                      <Badge colorScheme={mappings.sell_mode === 'per_person' ? 'teal' : 'gray'}>
                        {mappings.sell_mode === 'per_person'
                          ? t('channelManager.mappings.pricingPerGuestCount')
                          : t('channelManager.mappings.pricingPerRoom')}
                      </Badge>
                      {mappings.sell_mode !== mappings.provisioned_sell_mode && (
                        <Badge colorScheme="orange">
                          {t('channelManager.mappings.sellModeStale', { mode: mappings.provisioned_sell_mode })}
                        </Badge>
                      )}
                    </HStack>
                    <TableContainer>
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>{t('channelManager.mappings.rate')}</Th>
                            <Th>{t('channelManager.mappings.primary')}</Th>
                            <Th>{t('channelManager.mappings.active')}</Th>
                            <Th>{t('channelManager.mappings.priceFrom')}</Th>
                            <Th>{t('channelManager.mappings.adjustment')}</Th>
                            <Th>{t('channelManager.mappings.channexUuid')}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {mappings.rate_plans.map((m) => (
                            <Tr key={m.id} opacity={m.is_active ? 1 : 0.5}>
                              <Td fontWeight={m.is_primary ? 'semibold' : 'normal'}>{m.label}</Td>
                              <Td>
                                {m.is_primary ? (
                                  <Badge colorScheme="blue">{t('channelManager.mappings.primaryBadge')}</Badge>
                                ) : (
                                  <Button size="xs" variant="link"
                                    onClick={() => editRateMapping(m, { is_primary: true })}>
                                    {t('channelManager.mappings.makePrimary')}
                                  </Button>
                                )}
                              </Td>
                              <Td>
                                <Switch size="sm" isChecked={m.is_active}
                                  onChange={(e) => editRateMapping(m, { is_active: e.target.checked })} />
                              </Td>
                              <Td>
                                <Tooltip label={t('channelManager.tooltips.priceFrom')}>
                                  <Select size="xs" w="96px" value={m.price_from_occupancy || ''}
                                    onChange={(e) => editRateMapping(m, { price_from_occupancy: e.target.value || null })}>
                                    <option value="">{t('channelManager.mappings.ownPrice', { adults: m.num_adults })}</option>
                                    {Array.from({ length: m.max_adults || m.num_adults }, (_, i) => i + 1)
                                      .filter((n) => n !== m.num_adults)
                                      .map((n) => (
                                        <option key={n} value={n}>
                                          {t('channelManager.mappings.adultsPrice', { adults: n })}
                                        </option>
                                      ))}
                                  </Select>
                                </Tooltip>
                              </Td>
                              <Td>
                                <HStack spacing={1}>
                                  <Select size="xs" w="110px" value={m.rate_adjustment_type}
                                    onChange={(e) => editRateMapping(m, { rate_adjustment_type: e.target.value })}>
                                    <option value="static">{t('channelManager.mappings.adjustStatic')}</option>
                                    <option value="multiplier">{t('channelManager.mappings.adjustMultiplier')}</option>
                                    <option value="both">{t('channelManager.mappings.adjustBoth')}</option>
                                  </Select>
                                  {m.rate_adjustment_type !== 'multiplier' && (
                                    <Input size="xs" w="70px" defaultValue={m.static_rate_amount}
                                      title={t('channelManager.mappings.staticAmountTitle')}
                                      onBlur={(e) => e.target.value !== m.static_rate_amount
                                        && editRateMapping(m, { static_rate_amount: e.target.value })} />
                                  )}
                                  {m.rate_adjustment_type !== 'static' && (
                                    <Input size="xs" w="70px" defaultValue={m.rate_multiplier}
                                      title={t('channelManager.mappings.multiplierTitle')}
                                      onBlur={(e) => e.target.value !== m.rate_multiplier
                                        && editRateMapping(m, { rate_multiplier: e.target.value })} />
                                  )}
                                </HStack>
                              </Td>
                              <Td>
                                {m.pending
                                  ? <Badge colorScheme="yellow">{t('channelManager.mappings.pendingSync')}</Badge>
                                  : <Code fontSize="xs">{m.channel_rate_plan_code}</Code>}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={mappingModal.onClose}>{t('channelManager.buttons.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AddHotelModal
        isOpen={addHotelModal.isOpen}
        onClose={addHotelModal.onClose}
        onDone={load}
      />

      <ConnectChannelModal
        isOpen={connectModal.isOpen}
        onClose={connectModal.onClose}
        config={selected}
        onDone={load}
      />

      <LoggedBookingsModal
        isOpen={loggedBookingsModal.isOpen}
        onClose={loggedBookingsModal.onClose}
        config={selected}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.onClose}
        title={confirmAction?.label}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.label}
        colorScheme="blue"
        onConfirm={runConfirmed}
      />
    </PageShell>
  );
};

export default ChannelManager;
